/**
 * `versionSDK` ของแอปมือถือ — ฝั่ง frontend (ช่วง SDK ของ "ชุดการ์ดตาม SDK version" บน node Mutual Fund).
 *
 * รูปค่าจากแอปคือ `"v75.44"` — 75 = เวอร์ชัน SDK จริง, 44 = เวอร์ชัน flow · ทุกจุดดูเฉพาะเลขหน้าจุด
 * กติกาตรงกับ `BAL/BAL/FlowEngine/SdkVersion.cs` (`ParseMajor`, `TryParseNumber`, `InRange`) — แก้ฝั่งหนึ่งต้องแก้อีกฝั่ง
 *
 * ── กติกาช่วงของแถว (ฝั่งรันอยู่ `MutualFundCardTurn.ResolveCardSlots`)
 * inclusive · ว่างด้านใด = ไม่จำกัดด้านนั้น · ว่างทั้งคู่ = ไม่ตรงกับแอปไหน · from > to = ไม่ตรงกับแอปไหน
 * ทับกัน = แถวบนชนะ
 *
 * 🔴 ไฟล์นี้ต้องไม่พึ่งโมดูลอื่น (ไม่มี alias `@/`, ไม่อ่าน env ของ vite, ไม่มี vue) — `node --test` รันตรง ๆ
 */

/**
 * เลขที่ใหญ่ที่สุดที่ backend แปลงได้ (C# `int`).
 * 🔴 เลขที่เกินนี้ backend แปลงไม่ได้แล้วถือเป็น "ไม่จำกัด" ⇒ แถวกว้างกว่าที่ตั้งใจ จึงต้องไม่ถูกเขียนลง node data
 */
export const SDK_NUMBER_MAX = 2147483647;

function withinBackendRange(number) {
  return number <= SDK_NUMBER_MAX ? number : null;
}

/**
 * เลขหน้าจุด: trim · ตัด `v`/`V` นำหน้าได้ 1 ตัว · ASCII digit ติดกันอย่างน้อย 1 ตัว · ตัวถัดไปต้องเป็น `.` หรือจบสตริง
 * `"v75.44"`/`"75"`/`"V75"`/`"v075.1"`/`"75.44.1"`/`" v75.44 "` ⇒ 75
 * · `"v.44"`/`"abc"`/`""`/null/`"v75a"`/`"v 75"`/`"-1"`/เลขที่ไม่ใช่ ASCII/ค่าเกิน int ⇒ null
 * @param {unknown} raw
 * @returns {number|null}
 */
export function parseSdkMajor(raw) {
  // แอปอาจส่งเป็น number บน wire — ฝั่ง C# ก็แปลงเป็นข้อความก่อน (`SdkVersion.ReadRaw`)
  const text = typeof raw === 'number' ? String(raw) : raw;
  if (typeof text !== 'string') {
    return null;
  }
  const match = /^[vV]?([0-9]+)(?:\.|$)/.exec(text.trim());
  return match ? withinBackendRange(Number(match[1])) : null;
}

/**
 * ค่าขอบช่วงจาก node data / ช่องกรอก: ว่าง/null ⇒ null · ASCII digit ล้วน ⇒ number · จำนวนเต็ม ≥ 0 ⇒ ค่านั้น
 * · อื่น ๆ/ค่าเกิน int ⇒ null (อ่านแบบเดียวกับ `SdkVersion.TryParseNumber` คือ "ไม่จำกัดด้านนี้")
 * @param {unknown} raw
 * @returns {number|null}
 */
export function parseSdkNumber(raw) {
  if (typeof raw === 'number') {
    return Number.isInteger(raw) && raw >= 0 ? withinBackendRange(raw) : null;
  }
  if (typeof raw !== 'string') {
    return null;
  }
  const text = raw.trim();
  return /^[0-9]+$/.test(text) ? withinBackendRange(Number(text)) : null;
}

/**
 * ข้อความช่วงของแถว: `"75–79"` · `"≥ 80"` · `"≤ 74"` · ว่างทั้งคู่ `"(ว่าง)"`
 * อ่านผ่าน `parseSdkNumber` จึงแสดงช่วงเดียวกับที่ฝั่งรันจะอ่านได้จริง
 * @returns {string}
 */
export function formatSdkRange(from, to) {
  const low = parseSdkNumber(from);
  const high = parseSdkNumber(to);
  if (low === null && high === null) {
    return '(ว่าง)';
  }
  if (high === null) {
    return `≥ ${low}`;
  }
  if (low === null) {
    return `≤ ${high}`;
  }
  return `${low}–${high}`;
}

function rangesOverlap(a, b) {
  const lowest = Math.max(a.low ?? -Infinity, b.low ?? -Infinity);
  const highest = Math.min(a.high ?? Infinity, b.high ?? Infinity);
  return lowest <= highest;
}

/**
 * คำเตือนของรายการแถว (ไม่บล็อกเซฟ) — เรียงตามแถว · `index`/`withIndex` เริ่มที่ 0 · ข้อความนับแถวเริ่มที่ 1
 * - `empty` ว่างทั้งคู่ · `inverted` from > to — ทั้งสองแบบไม่ตรงกับแอปไหน และไม่นำไปเทียบการทับกัน
 * - `overlap` ทับกับแถวก่อนหน้า รายงานที่แถวล่าง 1 รายการต่อ 1 แถวบนที่ทับ
 * @param {unknown} sets
 * @returns {Array<{index: number, kind: 'empty'|'inverted'|'overlap', withIndex?: number, message: string}>}
 */
export function cardSetRangeIssues(sets) {
  if (!Array.isArray(sets)) {
    return [];
  }
  const issues = [];
  const usable = [];
  sets.forEach((set, index) => {
    const low = parseSdkNumber(set?.sdkFrom);
    const high = parseSdkNumber(set?.sdkTo);
    const rowName = `แถวที่ ${index + 1}`;
    if (low === null && high === null) {
      issues.push({
        index,
        kind: 'empty',
        message: `${rowName} ยังไม่กำหนดช่วง SDK — แถวนี้จะไม่ตรงกับแอปไหนเลย`,
      });
      return;
    }
    const label = formatSdkRange(low, high);
    if (low !== null && high !== null && low > high) {
      issues.push({
        index,
        kind: 'inverted',
        message: `${rowName} ช่วง SDK ${label} ค่าเริ่มมากกว่าค่าสุดท้าย — แถวนี้จะไม่ตรงกับแอปไหนเลย`,
      });
      return;
    }
    const current = { index, low, high, label };
    usable.filter((earlier) => rangesOverlap(earlier, current)).forEach((earlier) => {
      issues.push({
        index,
        kind: 'overlap',
        withIndex: earlier.index,
        message: `${rowName} (SDK ${label}) ทับกับแถวที่ ${earlier.index + 1} (SDK ${earlier.label}) — เลขที่ตรงทั้งคู่ใช้แถวที่ ${earlier.index + 1} เพราะแถวบนชนะ`,
      });
    });
    usable.push(current);
  });
  return issues;
}
